import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, CONTACT_INFO } from '../data/mock';
import { Link } from 'react-router-dom';
import { Eye, Shield, Truck, Award, Heart, Users, Clock, Check, ArrowRight, Star, Zap } from 'lucide-react';

const WhyChooseUsPage = () => {
  useEffect(() => {
    document.title = 'Why Choose Us | Clarity Lens Co';
  }, []);

  const reasons = [
    {
      icon: Eye,
      title: 'Premium Quality',
      description: 'Every pair of eyewear is crafted with meticulous attention to detail using only the finest materials. We never compromise on quality.'
    },
    {
      icon: Shield,
      title: 'Advanced Protection',
      description: 'All our lenses feature UV400 protection and specialized coatings to keep your eyes safe from harmful rays and screen strain.'
    },
    {
      icon: Truck,
      title: 'Fast & Free Shipping',
      description: 'Enjoy free shipping on orders over $75. All orders are processed within 24 hours and delivered within 3-5 business days.'
    },
    {
      icon: Award,
      title: '30-Day Guarantee',
      description: "Not satisfied? Return within 30 days for a full refund. No questions asked. We stand behind everything we sell."
    },
    {
      icon: Heart,
      title: 'Customer First',
      description: "Your satisfaction is our priority. Our dedicated support team is here to help you find the perfect eyewear for your needs."
    },
    {
      icon: Zap,
      title: '1-Year Warranty',
      description: 'All eyewear comes with a comprehensive 1-year warranty against manufacturing defects. We\'ve got you covered.'
    }
  ];

  const stats = [
    { value: '50K+', label: 'Happy Customers' },
    { value: '99%', label: 'Satisfaction Rate' },
    { value: '24hr', label: 'Order Processing' },
    { value: '1 Year', label: 'Warranty Coverage' }
  ];

  const values = [
    {
      title: 'Quality Over Quantity',
      desc: 'We carefully curate our collection, ensuring every product meets our strict quality standards before reaching you.'
    },
    {
      title: 'Transparent Pricing',
      desc: 'No hidden fees, no surprises. The price you see is the price you pay. Quality eyewear at fair prices.'
    },
    {
      title: 'Sustainable Practices',
      desc: 'We use eco-friendly packaging and partner with suppliers who share our commitment to environmental responsibility.'
    },
    {
      title: 'Continuous Innovation',
      desc: 'We stay at the forefront of eyewear technology, constantly updating our collection with the latest innovations.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero Banner */}
        <section className="relative h-[280px] overflow-hidden bg-neutral-100">
          <img 
            src={siteImages.featuredBackground}
            alt="Why Choose Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4">
                  <Star className="w-3.5 h-3.5" />
                  Our Promise
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold tracking-tight mb-2">
                  Why Choose Clarity Lens Co
                </h1>
                <p className="text-white/80">The difference is in the details</p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-3xl font-display font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-neutral-400 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reasons Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-neutral-400 text-sm font-medium uppercase tracking-wider mb-3 block">
                The Clarity Lens Difference
              </span>
              <h2 className="font-display text-3xl text-neutral-900 font-semibold tracking-tight">
                What Sets Us Apart
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reasons.map((reason, i) => (
                <div key={i} className="bg-neutral-50 rounded-2xl p-8 hover:shadow-card transition-all">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-5 shadow-sm">
                    <reason.icon className="w-7 h-7 text-neutral-700" />
                  </div>
                  <h3 className="font-medium text-neutral-900 text-lg mb-3">{reason.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-20 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-neutral-400 text-sm font-medium uppercase tracking-wider mb-3 block">
                  Our Values
                </span>
                <h2 className="font-display text-3xl text-neutral-900 font-semibold tracking-tight mb-8">
                  Built on Trust & Quality
                </h2>
                <div className="space-y-6">
                  {values.map((value, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-neutral-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-neutral-900 mb-1">{value.title}</h4>
                        <p className="text-neutral-600 text-sm leading-relaxed">{value.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img 
                  src={siteImages.productShowcase2}
                  alt="Quality eyewear"
                  className="rounded-2xl w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Trust Statement */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-16 bg-neutral-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Eye className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-4">
              "Our mission is simple: provide premium eyewear that combines exceptional quality, thoughtful design, and outstanding value."
            </h2>
            <p className="text-neutral-500">— The Clarity Lens Co Team</p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-neutral-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl text-white font-semibold mb-4">
              Ready to Experience the Difference?
            </h2>
            <p className="text-neutral-400 mb-10">
              Join thousands of satisfied customers who trust Clarity Lens Co
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-neutral-100 text-neutral-900 font-medium rounded-full transition-colors"
              >
                Shop Now
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/guarantee"
                className="inline-flex items-center gap-2 px-8 py-4 border border-neutral-700 hover:border-neutral-500 text-white font-medium rounded-full transition-colors"
              >
                View Our Guarantee
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default WhyChooseUsPage;
